/**
 * View-local UI state persistence convention.
 *
 * Storage key: `dolmit_<view>_<key>` (camelCase suffix). Values are
 * JSON-encoded; read tolerates parse failures by returning the supplied
 * fallback. This is for purely-rendering view state (display unit, sort,
 * filter chips). Server-driving state (rangeStart/rangeEnd/mode) lives
 * in `userPrefs` and is unaffected by this helper.
 */
import { useCallback, useState } from "react";

function buildKey(view: string, key: string): string {
  return `dolmit_${view}_${key}`;
}

export function read<T>(view: string, key: string, fallback: T): T {
  if (typeof localStorage === "undefined") return fallback;
  const raw = localStorage.getItem(buildKey(view, key));
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function write(view: string, key: string, value: unknown): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(buildKey(view, key), JSON.stringify(value));
  } catch {
    // Quota or disabled storage — silently drop. View state is best-effort.
  }
}

/**
 * React hook: same shape as `useState`, mirrored into `localStorage` under
 * `dolmit_<view>_<key>`. The initial render seeds from storage; every setter
 * call writes through. Functional updates are supported.
 */
export function usePersistedState<T>(
  view: string,
  key: string,
  fallback: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => read<T>(view, key, fallback));
  const setPersisted = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        write(view, key, resolved);
        return resolved;
      });
    },
    [view, key],
  );
  return [value, setPersisted];
}
